import { createApi } from '@reduxjs/toolkit/query/react';
import { Endpoint } from 'services/endpoints';

import { AccountUuid, ApiKeyListItem, ApiKeyPayload, CreateApiKeyResponse, GetApiKeysResponse } from 'types/apiKeys';
import HTTPMethod, { Response } from 'types/http';

import { fetchBaseQueryWithAuth } from '../api';

const REDUCER_PATH = 'apiKeysApi';
const TAGS = {
  API_KEYS: 'apiKeys',
};

export const apiKeysApi = createApi({
  reducerPath: REDUCER_PATH,
  baseQuery: fetchBaseQueryWithAuth,
  tagTypes: [TAGS.API_KEYS],
  endpoints: (builder) => ({
    getApiKeysList: builder.query<GetApiKeysResponse, AccountUuid>({
      query: (accountUuid) => ({
        url: Endpoint.ApiKeys.List(accountUuid),
      }),
      transformResponse: (response: Response<GetApiKeysResponse>) => {
        if (!response.data) {
          throw new Error('Backend response most likely changed. Missing data in response');
        }
        return response.data;
      },
      providesTags: [TAGS.API_KEYS],
    }),
    createApiKey: builder.mutation<CreateApiKeyResponse, ApiKeyPayload & { accountUuid: AccountUuid }>({
      query: ({ accountUuid, ...body }) => ({
        url: Endpoint.ApiKeys.Create(accountUuid),
        method: HTTPMethod.POST,
        body,
      }),
      transformResponse: (response: Response<CreateApiKeyResponse>) => {
        if (!response.data) {
          throw new Error('Backend response most likely changed. Missing data in response');
        }
        return response.data;
      },
      invalidatesTags: (result, error) => (error ? [] : [TAGS.API_KEYS]),
    }),
    updateApiKey: builder.mutation<ApiKeyListItem, ApiKeyPayload & { accountUuid: AccountUuid; apiKeyUuid: string }>({
      query: ({ accountUuid, apiKeyUuid, ...body }) => ({
        url: Endpoint.ApiKeys.Update(accountUuid, apiKeyUuid),
        method: HTTPMethod.PUT,
        body,
      }),
      transformResponse: (response: Response<ApiKeyListItem>) => {
        if (!response.data) {
          throw new Error('Backend response most likely changed. Missing data in response');
        }
        return response.data;
      },
      invalidatesTags: (result, error) => (error ? [] : [TAGS.API_KEYS]),
    }),
    deleteApiKey: builder.mutation<void, { accountUuid: AccountUuid; apiKeyUuid: string }>({
      query: ({ accountUuid, apiKeyUuid }) => ({
        url: Endpoint.ApiKeys.Delete(accountUuid, apiKeyUuid),
        method: HTTPMethod.DELETE,
      }),
      invalidatesTags: (result, error) => (error ? [] : [TAGS.API_KEYS]),
    }),
  }),
});

export const { getApiKeysList } = apiKeysApi.endpoints;

export const {
  useGetApiKeysListQuery,
  useLazyGetApiKeysListQuery,
  useCreateApiKeyMutation,
  useUpdateApiKeyMutation,
  useDeleteApiKeyMutation,
} = apiKeysApi;

export default apiKeysApi;
